import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Plus, Trash2, Save, Eye } from 'lucide-react';

interface OrgNode {
  id: string;
  name: { ar: string; en: string };
  title: { ar: string; en: string };
  email: string;
  parentId: string | null;
  order: number;
}

interface OrgChartBuilderProps {
  data: OrgNode[];
  onChange: (data: OrgNode[]) => void;
}

export function OrgChartBuilder({ data, onChange }: OrgChartBuilderProps) {
  const [nodes, setNodes] = useState<OrgNode[]>(data);
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    setNodes(data);
  }, [data]);

  const addNode = () => {
    const newNode: OrgNode = {
      id: `node-${Date.now()}`,
      name: { ar: '', en: '' },
      title: { ar: '', en: '' },
      email: '',
      parentId: selectedNode,
      order: nodes.length + 1,
    };
    const updatedNodes = [...nodes, newNode];
    setNodes(updatedNodes);
    onChange(updatedNodes);
    setSelectedNode(newNode.id);
  };

  const updateNode = (id: string, field: string, value: any) => {
    const updatedNodes = nodes.map(node =>
      node.id === id ? { ...node, [field]: value } : node
    );
    setNodes(updatedNodes);
    onChange(updatedNodes);
  };

  const deleteNode = (id: string) => {
    // Delete node and all its children
    const nodesToDelete = new Set([id]);
    let changed = true;
    
    while (changed) {
      changed = false;
      nodes.forEach(node => {
        if (node.parentId && nodesToDelete.has(node.parentId) && !nodesToDelete.has(node.id)) {
          nodesToDelete.add(node.id);
          changed = true;
        }
      });
    }

    const updatedNodes = nodes.filter(node => !nodesToDelete.has(node.id));
    setNodes(updatedNodes);
    onChange(updatedNodes);
    
    if (selectedNode && nodesToDelete.has(selectedNode)) {
      setSelectedNode(null);
    }
  };

  const renderTree = (parentId: string | null = null, level: number = 0): JSX.Element[] => {
    return nodes
      .filter(node => node.parentId === parentId)
      .sort((a, b) => a.order - b.order)
      .map(node => (
        <div key={node.id} style={{ marginLeft: `${level * 30}px` }} className="my-2">
          <div
            className={`flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 ${
              selectedNode === node.id ? 'bg-blue-50 border-blue-500' : 'bg-white'
            }`}
            onClick={() => setSelectedNode(node.id)}
          >
            <div className="flex-1">
              <p className="font-semibold text-sm">{typeof node.title === 'string' ? node.title : (node.title.ar || 'بدون منصب')}</p>
              <p className="text-xs text-gray-600">{typeof node.name === 'string' ? node.name : (node.name.ar || 'بدون اسم')}</p>
              {node.email && <p className="text-xs text-gray-500">{node.email}</p>}
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                deleteNode(node.id);
              }}
              className="h-8 w-8"
            >
              <Trash2 className="h-4 w-4 text-red-500" />
            </Button>
          </div>
          {renderTree(node.id, level + 1)}
        </div>
      ));
  };

  const selectedNodeData = nodes.find(n => n.id === selectedNode);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Tree View */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">الهيكل التنظيمي</h3>
          <Button onClick={addNode} size="sm" className="bg-[#7dda55] hover:bg-[#6bc444]">
            <Plus className="h-4 w-4 mr-2" />
            إضافة عنصر
          </Button>
        </div>
        <div className="max-h-[600px] overflow-y-auto">
          {nodes.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p>لا توجد عناصر</p>
              <p className="text-sm mt-2">انقر على "إضافة عنصر" للبدء</p>
            </div>
          ) : (
            renderTree()
          )}
        </div>
      </Card>

      {/* Edit Panel */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">تعديل العنصر</h3>
        {selectedNodeData ? (
          <div className="space-y-4">
            <div className="space-y-3">
              <Label className="text-base font-semibold">المنصب / Position</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="title-ar" className="text-sm text-muted-foreground">عربي</Label>
                  <Input
                    id="title-ar"
                    value={typeof selectedNodeData.title === 'string' ? selectedNodeData.title : selectedNodeData.title.ar}
                    onChange={(e) => updateNode(selectedNode!, 'title', { ...(typeof selectedNodeData.title === 'string' ? { ar: selectedNodeData.title, en: '' } : selectedNodeData.title), ar: e.target.value })}
                    placeholder="المدير التنفيذي"
                    dir="rtl"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="title-en" className="text-sm text-muted-foreground">English</Label>
                  <Input
                    id="title-en"
                    value={typeof selectedNodeData.title === 'string' ? '' : selectedNodeData.title.en}
                    onChange={(e) => updateNode(selectedNode!, 'title', { ...(typeof selectedNodeData.title === 'string' ? { ar: selectedNodeData.title, en: '' } : selectedNodeData.title), en: e.target.value })}
                    placeholder="Executive Director"
                    dir="ltr"
                  />
                </div>
              </div>
            </div>
            <div className="space-y-3">
              <Label className="text-base font-semibold">الاسم / Name</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name-ar" className="text-sm text-muted-foreground">عربي</Label>
                  <Input
                    id="name-ar"
                    value={typeof selectedNodeData.name === 'string' ? selectedNodeData.name : selectedNodeData.name.ar}
                    onChange={(e) => updateNode(selectedNode!, 'name', { ...(typeof selectedNodeData.name === 'string' ? { ar: selectedNodeData.name, en: '' } : selectedNodeData.name), ar: e.target.value })}
                    placeholder="د. محمد أحمد"
                    dir="rtl"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name-en" className="text-sm text-muted-foreground">English</Label>
                  <Input
                    id="name-en"
                    value={typeof selectedNodeData.name === 'string' ? '' : selectedNodeData.name.en}
                    onChange={(e) => updateNode(selectedNode!, 'name', { ...(typeof selectedNodeData.name === 'string' ? { ar: selectedNodeData.name, en: '' } : selectedNodeData.name), en: e.target.value })}
                    placeholder="Dr. Mohammed Ahmed"
                    dir="ltr"
                  />
                </div>
              </div>
            </div>
            <div>
              <Label htmlFor="email">البريد الإلكتروني</Label>
              <Input
                id="email"
                type="email"
                value={selectedNodeData.email}
                onChange={(e) => updateNode(selectedNode!, 'email', e.target.value)}
                placeholder="example@namat.org.sa"
                dir="ltr"
              />
            </div>
            <div>
              <Label htmlFor="parent">العنصر الأب</Label>
              <select
                id="parent"
                value={selectedNodeData.parentId || ''}
                onChange={(e) => updateNode(selectedNode!, 'parentId', e.target.value || null as any)}
                className="w-full border rounded-md p-2"
                dir="rtl"
              >
                <option value="">بدون (عنصر رئيسي)</option>
                {nodes
                  .filter(n => n.id !== selectedNode)
                  .map(node => (
                    <option key={node.id} value={node.id}>
                      {typeof node.title === 'string' ? node.title : node.title.ar} - {typeof node.name === 'string' ? node.name : node.name.ar}
                    </option>
                  ))}
              </select>
            </div>
            <div className="pt-4 border-t">
              <Button
                variant="destructive"
                onClick={() => deleteNode(selectedNode!)}
                className="w-full"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                حذف العنصر
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <p>اختر عنصراً من الهيكل للتعديل</p>
          </div>
        )}
      </Card>
    </div>
  );
}

